<script setup>
import ReportFilterSelector from '../../settings/reports/components/FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';
import { useI18n } from 'vue-i18n';
import SummaryReportLink from '../../settings/reports/components/SummaryReportLink.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'agent',
  },
});

const store = useStore();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const channelId = ref(null);
const agentId = ref(null);

const rowItems = useMapGetter(['agents/getAgents']) || [];
const reportMetrics = useMapGetter(['gmAnalytics/getEnhancedAgentMetrics']) || [];

const getMetrics = id => {
  console.log(`🔍 GmEnhancedAgentMetricsTable: Getting metrics for agent ID: ${id}`);
  
  if (!reportMetrics.value || !Array.isArray(reportMetrics.value)) {
    console.log('⚠️ GmEnhancedAgentMetricsTable: reportMetrics is not an array:', reportMetrics.value);
    return {};
  }
  
  const found = reportMetrics.value.find(metrics => metrics.agent_id === Number(id));
  console.log(`🔍 GmEnhancedAgentMetricsTable: Found metrics for ID ${id}:`, found);
  return found || {};
};

const columnHelper = createColumnHelper();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

// Enhanced columns with all derived metrics
const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.AGENT'),
    width: 180,
    cell: cellProps => h(SummaryReportLink, cellProps),
  }),
  columnHelper.accessor('avgHandleTime', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.AHT'),
    width: 100,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('fcrRate', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.FCR'),
    width: 100,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('transferRate', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.TRANSFER_RATE'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('occupancyRate', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.OCCUPANCY'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('afterCallWorkTime', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.ACW'),
    width: 100,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('messagesPerConversation', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.MESSAGES_PER_CONV'),
    width: 140,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('responseRate', {
    header: t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.RESPONSE_RATE'),
    width: 120,
    cell: defaulSpanRender,
  }),
]);

// Enhanced rendering functions
const renderAvgTime = value => (value ? formatTime(value) : '--');
const renderCount = value => (value ? value.toLocaleString() : '--');
const renderPercentage = value => (value ? `${value.toFixed(1)}%` : '--');
const renderDecimal = value => (value ? value.toFixed(2) : '--');

const renderSentiment = value => {
  if (!value) return '--';
  const score = parseFloat(value);
  if (score >= 0.7) return `${(score * 100).toFixed(1)}% 😊`;
  if (score >= 0.4) return `${(score * 100).toFixed(1)}% 😐`;
  return `${(score * 100).toFixed(1)}% 😞`;
};

const renderCSAT = value => {
  if (!value) return '--';
  return `${(value * 100).toFixed(1)}%`;
};

const renderOccupancy = value => {
  if (!value) return '--';
  const percentage = value; // API already returns percentage
  if (percentage >= 80) return `${percentage.toFixed(1)}% 🔥`;
  if (percentage >= 60) return `${percentage.toFixed(1)}% ⚡`;
  if (percentage >= 40) return `${percentage.toFixed(1)}% 📊`;
  return `${percentage.toFixed(1)}% 😴`;
};

const renderFCR = value => {
  if (!value) return '--';
  const percentage = value; // API already returns percentage
  if (percentage >= 80) return `${percentage.toFixed(1)}% 🎯`;
  if (percentage >= 60) return `${percentage.toFixed(1)}% ✅`;
  if (percentage >= 40) return `${percentage.toFixed(1)}% ⚠️`;
  return `${percentage.toFixed(1)}% ❌`;
};

const tableData = computed(() => {
  console.log('🔍 GmEnhancedAgentMetricsTable: Computing table data...');
  console.log('🔍 GmEnhancedAgentMetricsTable: rowItems.value:', rowItems.value);
  console.log('🔍 GmEnhancedAgentMetricsTable: reportMetrics.value:', reportMetrics.value);
  
  return rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    console.log(`🔍 GmEnhancedAgentMetricsTable: Agent ${row.id} (${row.name}) metrics:`, rowMetrics);
    
    // Map the backend field names to frontend field names
    const {
      conversations_count,
      avg_handle_time,
      fcr_rate,
      transfer_rate,
      occupancy_rate,
      avg_first_response_time,
      avg_resolution_time,
      after_call_work_time,
      messages_per_conversation,
      response_rate,
      avg_sentiment_score,
      avg_customer_satisfaction,
    } = rowMetrics;
    
    const mappedData = {
      id: row.id,
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversations_count),
      avgHandleTime: renderAvgTime(avg_handle_time),
      fcrRate: renderFCR(fcr_rate),
      transferRate: renderPercentage(transfer_rate),
      occupancyRate: renderOccupancy(occupancy_rate),
      avgFirstResponseTime: renderAvgTime(avg_first_response_time),
      avgResolutionTime: renderAvgTime(avg_resolution_time),
      afterCallWorkTime: renderAvgTime(after_call_work_time),
      messagesPerConversation: renderDecimal(messages_per_conversation),
      responseRate: renderPercentage(response_rate),
      sentimentScore: renderSentiment(avg_sentiment_score),
      customerSatisfaction: renderCSAT(avg_customer_satisfaction),
    };
    
    console.log(`✅ GmEnhancedAgentMetricsTable: Mapped data for ${row.name}:`, mappedData);
    return mappedData;
  });
});

const fetchAllData = async () => {
  console.log('🔄 GmEnhancedAgentMetricsTable: Starting to fetch data...');
  
  try {
    // Fetch agents first
    console.log('👥 GmEnhancedAgentMetricsTable: Fetching agents...');
    await store.dispatch('agents/get');
    console.log('✅ GmEnhancedAgentMetricsTable: Agents fetched');
    
    // Then fetch enhanced agent metrics data
    console.log('📊 GmEnhancedAgentMetricsTable: Fetching enhanced agent metrics...');
    
    const filterParams = {
      dateRange: {
        from: from.value,
        to: to.value,
        preset: 'last_30_days' // Default preset
      },
      period: {},
      businessHours: businessHours.value,
      channelId: channelId.value,
      agentId: agentId.value,
    };
    
    console.log('🔧 GmEnhancedAgentMetricsTable: Dispatching with params:', filterParams);
    
    const performanceData = await store.dispatch('gmAnalytics/fetchEnhancedAgentMetrics', filterParams);
    console.log('✅ GmEnhancedAgentMetricsTable: Enhanced metrics data fetched:', performanceData);
  } catch (error) {
    console.error('❌ GmEnhancedAgentMetricsTable: Error fetching data:', error);
  }
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  console.log('🔧 GmEnhancedAgentMetricsTable: Filter change received:', updatedFilter);
  console.log('🔧 GmEnhancedAgentMetricsTable: selectedInbox:', updatedFilter.selectedInbox);
  console.log('🔧 GmEnhancedAgentMetricsTable: selectedAgents:', updatedFilter.selectedAgents);
  
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  channelId.value = updatedFilter.selectedInbox ? updatedFilter.selectedInbox.id : null;
  agentId.value = updatedFilter.selectedAgents && updatedFilter.selectedAgents.length > 0 ? updatedFilter.selectedAgents[0].id : null;
  
  console.log('🔧 GmEnhancedAgentMetricsTable: Set channelId to:', channelId.value);
  console.log('🔧 GmEnhancedAgentMetricsTable: Set agentId to:', agentId.value);
  
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method for enhanced metrics
const downloadReports = () => {
  const fileName = generateFileName({
    type: 'enhanced_agent_metrics',
    to: to.value,
    businessHours: businessHours.value,
  });
  const params = {
    from: from.value,
    to: to.value,
    fileName,
    businessHours: businessHours.value,
  };
  store.dispatch('gmAnalytics/downloadEnhancedAgentMetrics', params);
};

defineExpose({ downloadReports });
</script>

<template>
  <div class="enhanced-agent-metrics-section">
    <!-- Section Header -->
    <div class="mb-6">
      <h2 class="text-xl font-semibold text-slate-800 mb-2">
        {{ $t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.HEADER') }}
      </h2>
      <p class="text-sm text-slate-600">
        {{ $t('GM_ANALYTICS.ENHANCED_AGENT_METRICS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Filter Controls -->
    <ReportFilterSelector 
      :show-inbox-filter="true"
      :show-agents-filter="true"
      @filter-change="onFilterChange" 
    />
    
    <!-- Enhanced Metrics Table -->
    <div
      class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <div class="overflow-x-auto">
        <Table :table="table" class="min-w-full enhanced-metrics-table" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.enhanced-agent-metrics-section {
  margin-bottom: 2rem;
}

.enhanced-metrics-table {
  font-size: 0.875rem;
}

.enhanced-metrics-table :deep(.table-cell) {
  padding: 0.5rem 0.75rem;
  white-space: nowrap;
}

.enhanced-metrics-table :deep(.table-header) {
  padding: 0.75rem 0.75rem;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}

.enhanced-metrics-table :deep(.table-row:hover) {
  background-color: #f1f5f9;
}

.enhanced-metrics-table :deep(.table-row:nth-child(even)) {
  background-color: #fafbfc;
}
</style>
